import { AppContext } from "App";

import { AggregationFunction } from "@multiweb/clients/dataReader";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { ConditionParams, IDataSourceLoadInfo, IDBDataSourceParams, IOrderBy, OrderType } from "../../../../../Global/Base/DataSources/Interfaces";
import { OWDate } from "../../../../../Global/Base/DataTypes/OWDate";
import { OWTime } from "../../../../../Global/Base/DataTypes/OWTime";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event, StatefulEvent } from "../../../../../Global/Base/Events";
import { InvalidOperationException, NotImplementedException } from "../../../../../Global/Base/Exceptions";
import { ArrayUtils } from "../../../../../Global/Base/Utils/ArrayUtils";
import { DateUtils } from "../../../../../Global/Base/Utils/DateUtils";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { Enum } from "../../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { ActionType } from "../../../../UI.Controls/Actions/IAction";
import { ChartElement, ChartType } from "../../../../UI.Controls/ChartElement";
import { IChartColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { EmptyDataGridMessage } from "../../../../UI.Controls/DataGridSubcontrols/DataGridMessages/EmptyDatagridMessage";
import { EmptyDatagridMessageType } from "../../../../UI.Controls/DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { DBDataSource } from "../../../../UI.Controls/DataSource/DBDataSource";
import { DropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownMenu";
import { createElement, registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { FormControlParams } from "../../Controls/ControlInterfaces";
import { ChartNameBuilder } from "../../DataBuilders/ChartNameBuilder";
import { IMenu } from "../../FormUI/FormUI";
import { ISystemOperationRights, SystemOperation } from "../../Helpers/SystemOperationRights";
import { IFrameProfileInfo } from "../../IFrameColumnInfo";
import { UIManager } from "../../UIManager";
import { StandardAction } from "../Actions/StandardAction";
import { MWColors } from "../Colors";
import { DropDownMenuCheckboxItem } from "../Filters/DropDownMenuItems/DropDownMenuCheckboxItem";
import { IChartProfileInfo } from "../Forms/IFormProfileInfo";
import { HideButtons, IActualObjectChangedEventArgs } from "../Frames";
import { IFrame, IFrameCtxMenuData } from "../IFrame";
import { ChartSettingsManager } from "./ChartSettingsManager";
import { ChartSettingsPanel } from "./ChartSettingsPanel";
import {
    IFrameWithProfile,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
    ISpecificallyLoadableFrame,
} from "./FrameInterfaces";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { GenericListFrame } from "./GenericListFrame";
import { MasterableFormControl } from "./MasterableFormControl";

export type ChartFrameParams = FormControlParams & {
    isInModalWindow: boolean;
};

export class ChartFrame extends MasterableFormControl implements IFrameWithProfile, IFrame, ISpecificallyLoadableFrame {
    public readonly frameProfileChanged: Event<IFrameWithProfile, void> = new Event<IFrameWithProfile, void>();
    public readonly selectedProfileChanged: Event<IFrameWithProfile, IFrameProfileInfo> = new Event<IFrameWithProfile, IFrameProfileInfo>();
    public readonly chartNameChanged: Event<IFrameWithProfile, IFrameProfileInfo> = new Event<IFrameWithProfile, IFrameProfileInfo>();

    private _emptyMessage: ChartEmptyDatagridMessage = new ChartEmptyDatagridMessage();
    private readonly _lengthTextForAxis: number = 25;
    protected _isInitialized: boolean = false;
    protected _initializationStarted: boolean = false;

    private _mainDiv: HTMLElement;
    private _chartElement: ChartElement;
    private _chartSettingsPanel: ChartSettingsPanel;
    private _settingsAction: StandardAction;
    private _saveAction: StandardAction;
    private _moreAction: StandardAction;
    private _chartSettingsManager: ChartSettingsManager;
    private _canEditProfile: boolean = false;
    private _condition: string;
    private _conditionParams?: ConditionParams;
    private _dropDownMenu: DropDownMenu;
    /** Příznak, že chartframe je otevřen v samostatném modálním okně */
    private _isInModalWindow: boolean = false;
    protected _interactive: boolean = true;
    private _classAttributeObserver: MutationObserver;
    private _chartColors: MWColors = MWColors.getInstance();

    // zde si nastavíme, jaké chceme mít barvy pro specifické prvky grafu (barva písma, mřížka v pozadí,...), hodnota se musí shodovat s className objektu barvy z MWColors
    private _fontColor = LessThemeSelector.blackThenWhite;
    private _gridLinesColor = "chart-lines"; // selector pro barvu "Chart lines"
    private _zeroLineColor = this._gridLinesColor;
    private _borderColor = LessThemeSelector.whiteThenBlack;

    //+ IFrameWithProfile
    public get profileInfo(): IFrameProfileInfo {
        return this._chartSettingsManager.chartSettings;
    }
    public set profileInfo(v: IFrameProfileInfo) {
        if (!(v instanceof IChartProfileInfo)) {
            this.clearProfileInfo();
            return;
        }

        this._chartSettingsManager.chartSettings = v;

        this.refreshData();
    }
    //- IFrameWithProfile

    //+ IFrame
    public readonly uuid: string;
    public isMainFrame: boolean = false;
    public frameName: string = null;
    private _actualObject: OWObject;
    public get actualObject(): OWObject {
        return this._actualObject;
    }
    public set actualObject(value: OWObject) {
        this._actualObject = value;
    }
    public renderStandardRowActions: boolean = true;
    public readonly actualObjectValueChanged: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    public isActive: boolean = true;
    private _hideButtons: HideButtons;
    public get hideButtons(): HideButtons {
        if (!this._hideButtons)
            this._hideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), (this.ownerForm && this.ownerForm.visibleOnlyDetail));

        return this._hideButtons;
    }
    public set hideButtons(v: HideButtons) {
        this._hideButtons = v;
    }
    public refreshObjectAfterSetActive: boolean = false;
    public refreshListAfterSetActive: boolean = false;
    public frameIsVisible: Event<IFrame, void> = new Event<IFrame, void>();
    public contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();
    public readonly actualObjectChanged: Event<IMasterableFrame, IActualObjectChangedEventArgs> = new Event<IMasterableFrame, IActualObjectChangedEventArgs>();
    public readonly contextActionsReady: Event<IMasterableFrame, string> = new Event<IMasterableFrame, string>();
    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();
    public hideToolBar: boolean = false;
    public menu: IMenu = null;
    public masterObject: OWObject = new OWObject(this.frameContext);
    protected override _masterFrame: IMasterableFrameWithDataGrid = null;

    public override get masterFrame(): IMasterableFrameWithDataGrid {
        return this._masterFrame;
    }
    public override set masterFrame(value: IMasterableFrameWithDataGrid) {
        this._masterFrame = value;
    }

    public masterFrameName: string = null; //název listFramu, pro který je graf otevřen
    public masterLinkToAttr: string = null;
    public masterAttr: string = null;
    public suppressSaveDialog: boolean = false;

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
    }
    //- IFrame

    constructor({ isInModalWindow = false, ...otherParams }: ChartFrameParams) {
        super(otherParams);

        this.element.setAttribute("data-tsccontrol", "ChartFrame");
        this.element.classList.add("dataGrid");

        this.locKey = this.element.getAttribute("data-captionlockey");
        if (!this.locKey)
            this.locKey = "GUI-1500"; //Graf
        this.frameName = this.element.getAttribute("data-frameName");
        this.masterFrameName = this.element.getAttribute("data-masterframe");
        this.masterLinkToAttr = this.element.getAttribute("data-masterlinktoattr");
        this._isInModalWindow = isInModalWindow;
        this._initializeClassAttributeObserver();
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.constructor.name);
    }

    /** Inicializace Mutation Observeru, který hlídá změnu atributu "class" elementu "html" */
    private _initializeClassAttributeObserver(): void {
        this._classAttributeObserver = new MutationObserver((mutationRecord) => this._onChangeAttribute(mutationRecord));
        this._classAttributeObserver.observe(document.querySelector("html"), { attributeFilter: [ "data-theme" ] });
    }

    /** Callback funkce Mutation Observeru, volá se, pokud dojde ke změně hodnoty "class" atributu elementu "html" */
    private _onChangeAttribute(mutationList: MutationRecord[]): void {
        mutationList.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
                this.refreshData();
            }
        });
    }

    public clearProfileInfo(): void {
        this._initializeChartSetting();

        if (this.chartNameChanged)
            this.chartNameChanged.trigger(this, this._chartSettingsManager.chartSettings);

        this.refreshData();
    }

    private _prepareChartOptions(): void {
        this._chartElement.chartOption.responsive = true;
        this._chartElement.chartOption.maintainAspectRatio = false;
        this._chartElement.type = this._chartSettingsManager.chartSettings.chartType;
        //za určitých okolností (změna nastavení, přejetí myší přes graf) dochází k chybě při vykreslování grafu. Vypnutí animace tuto chybu eliminuje
        this._chartElement.chartOption.animation = { duration: 0 };
        this._chartElement.chartOption.indexAxis = this._chartSettingsManager.chartSettings.horizontal ? "y" : "x";

        this._prepareScalesAndLegend(this._chartSettingsManager.chartSettings.chartType === ChartType.pie || this._chartSettingsManager.chartSettings.chartType === ChartType.doughnut);
        this._prepareLineOptions(this._chartSettingsManager.chartSettings.chartType === ChartType.line);
    }

    private _prepareScalesAndLegend(isPieOrDoughnutType: boolean): void {
        this._chartElement.chartOption.plugins = {
            legend: {
                display: this._chartSettingsManager.chartSettings.showLegend,
                position: "bottom",
                labels: { color: this._chartColors.getThemeColor(this._fontColor) },
            },
        };

        // nastavení os nemá pro koláčový graf význam
        if (isPieOrDoughnutType) {
            this._chartElement.chartOption.scales = {
                x: { display: false },
                y: { display: false },
            };
            return;
        }

        const axes = {
            gridLines: {
                color: this._chartColors.getThemeColor(this._gridLinesColor),
                zeroLineColor: this._chartColors.getThemeColor(this._zeroLineColor),
            },
            ticks: {
                fontColor: this._chartColors.getThemeColor(this._fontColor),
                beginAtZero: true,
                display: this._chartSettingsManager.chartSettings.showAxisValues,
            },
        };

        this._chartElement.chartOption.scales = {
            x: {
                ...axes,
                position: "bottom",
            },
            y: {
                ...axes,
                position: "left",
            },
        };
    }

    private _prepareLineOptions(isLineType: boolean): void {
        // vypnutí zaoblení křivky
        if (isLineType) {
            this._chartElement.chartOption.elements = {
                line: { tension: 0 },
            };
        }
    }

    private async _prepareDataForChartElement(dataSource: GraphDBDataSource, loadInfo: IDataSourceLoadInfo): Promise<void> {
        let borderColor = this._chartColors.getThemeColor(this._borderColor); // nastavení barvy borderu grafu, aby se shodoval s pozadím dle theme
        let borderWidth = 1;
        let chartType: ChartType;
        let backgroundColors;
        const maxColors = this._chartColors.primaryColors.length;

        const index = this._chartSettingsManager.chartSettings.dataSets.indexOf(dataSource.dataSet);
        const valueAttribute = dataSource.getAttrs().find(attr => attr !== this._chartSettingsManager.chartSettings.xAxisAttr);

        if (dataSource.dataSet.chartType)
            chartType = dataSource.dataSet.chartType;
        else
            chartType = this._chartSettingsManager.chartSettings.chartType;

        if (dataSource.dataSet.caption != null) {
            if (String(dataSource.dataSet.caption).length > this._lengthTextForAxis)
                dataSource.dataSet.caption = String(dataSource.dataSet.caption).substr(0, this._lengthTextForAxis) + "...";
        }

        //u koláčových grafů potřebuji barvu pro každou výseč
        if (chartType === ChartType.pie || chartType === ChartType.doughnut)
            backgroundColors = new Array<string>();
        else if (dataSource.dataSet.chartColor !== undefined)
            backgroundColors = this._chartColors.getThemeColor(dataSource.dataSet.chartColor);
        else
            backgroundColors = this._chartColors.getThemeColor(this._chartColors.primaryColors[index % maxColors].className);

        if (chartType === ChartType.line && !(backgroundColors instanceof Array))
            borderColor = backgroundColors;

        if (chartType === ChartType.bar)
            borderWidth = 0;

        const labels: Array<string> = new Array<string>();
        const values: Array<number> = new Array<number>();

        for (const row of loadInfo.dataGenerator()) {
            if (chartType === ChartType.pie || chartType === ChartType.doughnut)
                (backgroundColors as Array<string>).push(this._chartColors.getThemeColor(this._chartColors.primaryColors[values.length % maxColors].className));
            const label = AttrValueReader.read(row[this._chartSettingsManager.chartSettings.xAxisAttr]);
            let txt: string;
            if (label == undefined)
                txt = await Localization.getTextPromise("GUI-680278"); //Nevyplněno
            else if (label instanceof Enum)
                txt = label.toString();
            else if (label instanceof OWDate)
                txt = DateUtils.dateToString(label, "d");
            else if (label instanceof OWTime)
                txt = DateUtils.dateToString(label, "T");
            else if (label instanceof Date)
                txt = DateUtils.dateToString(label);
            else
                txt = label;

            if (String(txt).length > this._lengthTextForAxis)
                txt = String(txt).substr(0, this._lengthTextForAxis) + "...";

            labels.push(txt);

            const value = AttrValueReader.read(row[valueAttribute]);
            if (value)
                values.push(value);
            else
                values.push(0);
        }

        this._chartElement.labels = ArrayUtils.getUniqueArray(this._chartElement.labels.concat(labels));
        // Po odstranění duplikátů chceme odstranit i jejich hodnoty, aby se do grafu nepropsali s labelem - undefined
        const removedIndeces = this._getUniqueArrayRemovedIndeces(this._chartElement.labels.concat(labels));
        for (let i = values.length - 1; i >= 0; i--) {
            if (i in removedIndeces) {
                values.splice(removedIndeces[i], 1);
            }
        }

        this._chartElement.datasets[index] = {
            label: dataSource.dataSet.caption,
            data: values,
            fill: false,
            backgroundColor: backgroundColors,
            borderColor: borderColor,
            borderWidth: borderWidth,
            type: chartType,
        };
    }

    private _getUniqueArrayRemovedIndeces<T>(array: T[] | ReadonlyArray<T>): number[] {
        const u: any = {};
        const a: any[] = [];
        const removedIndeces: number[] = [];

        for (let i = 0, l = array.length; i < l; ++i) {
            if (u[array[i]] == 1) {
                removedIndeces.push(i);
                continue;
            }

            a.push(array[i]);
            u[array[i]] = 1;
        }

        return removedIndeces;
    }

    private _prepareDataSetCondition(dataSet: IChartColumnDefinition, globalCondition: string): string {
        let datasetCondition = globalCondition;
        if (dataSet.condition) {
            if (datasetCondition)
                datasetCondition = StringUtils.combineConditions(ConditionOperator.and, datasetCondition, dataSet.condition);
            else
                datasetCondition = dataSet.condition;
        }

        return datasetCondition;
    }

    private async _prepareGlobalCondition(): Promise<string> {
        let globalCondition = this._condition;
        if (globalCondition && this._chartSettingsManager.chartSettings.condition) {
            globalCondition = StringUtils.combineConditions(ConditionOperator.and, globalCondition, this._chartSettingsManager.chartSettings.condition);
        } else if (this._chartSettingsManager.chartSettings.condition)
            globalCondition = this._chartSettingsManager.chartSettings.condition;

        // přidá se podmínka vazby na nadřízený záznam
        if (this.masterLinkToAttr && this.masterFrame && this.masterFrame.actualObject && !this.masterFrame.actualObject.isNew) {
            let masterId: any;
            if (this.masterAttr != null && this.masterAttr !== "")
                masterId = await this.masterFrame.actualObject.getValue(this.masterAttr + ".ID");
            else
                masterId = this.masterFrame.actualObject.id;

            globalCondition = StringUtils.combineConditions(ConditionOperator.and, globalCondition, this.masterLinkToAttr + "=" + masterId); // v masterObject uz je koncovy objekt po uplatneni pripadneho MasterAttr
        }

        return globalCondition;
    }

    public async refreshData(): Promise<void> {
        if (!this._chartSettingsManager.chartSettings.xAxisAttr || this._chartSettingsManager.chartSettings.dataSets.length === 0) {
            this._emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
            this._emptyMessage.visible = true;
            this._chartElement.clear();
            return;
        }

        this._prepareChartOptions();

        let countLoaded = 0;

        this._emptyMessage.visible = false;
        this._chartElement.labels = [];
        this._chartElement.datasets = new Array(this._chartSettingsManager.chartSettings.dataSets.length);

        const globalCondition = await this._prepareGlobalCondition();

        for (let index = 0; index < this._chartSettingsManager.chartSettings.dataSets.length; index++) {
            const dataSet = this._chartSettingsManager.chartSettings.dataSets[index];
            //pokud není nastaven atribut, tak ten dataset přeskočím, aby nespadlo na chybu
            if (!dataSet.attrName)
                continue;

            let valueAttribute = dataSet.attrName;
            if (dataSet.aggregateFunction)
                valueAttribute = dataSet.aggregateFunction + "(" + valueAttribute + ")";

            const datasetCondition = this._prepareDataSetCondition(dataSet, globalCondition);

            const dataSource = new GraphDBDataSource({
                className: this._chartSettingsManager.chartSettings.objectName,
                dataSet,
                attributes: [this._chartSettingsManager.chartSettings.xAxisAttr, valueAttribute],
                condition: datasetCondition,
                conditionParams: this._conditionParams,
                orderBy: [{ attributeName: this._chartSettingsManager.chartSettings.xAxisAttr, order: OrderType.ascending } as IOrderBy],
                batchSize: -1,
                frameContext: this.frameContext,
                dataLength: this._lengthTextForAxis,
            });

            dataSource.groupBy = [this._chartSettingsManager.chartSettings.xAxisAttr];

            dataSource.dataLoaded.add(async (sender, prm) => {
                await this._prepareDataForChartElement(sender as GraphDBDataSource, prm);

                //až po nahrání všech dat se vykreslí graf
                countLoaded++;
                if (countLoaded === this._chartSettingsManager.chartSettings.dataSets.length) {
                    //pokud by nebyla nalezena žádná data, tak se nezobrazí ani graf
                    if (this._chartElement.datasets.find(d => d.data.length > 0))
                        this._chartElement.showChart();
                    else {
                        this._emptyMessage.messageType = EmptyDatagridMessageType.filterEmpty;
                        this._emptyMessage.visible = true;
                        this._chartElement.clear();
                    }
                }
            });

            dataSource.loadNewData();
        }
    }

    //+ IFrame
    public async refreshActualObject(): Promise<void> {
        throw new NotImplementedException();
    }
    public async waitForDataLoad(): Promise<boolean> {
        if (!this.masterFrame)
            return Promise.resolve(true);

        return this.masterFrame.waitForDataLoad();
    }
    public async waitForPendingUpdates(): Promise<void> {
        throw new NotImplementedException();
    }
    public async waitForActualObjectSet(): Promise<boolean> {
        if (this.masterFrame)
            await this.masterFrame.waitForActualObjectSet();

        return Promise.resolve(true);
    }
    //- IFrame

    //+ ISpecificallyLoadableFrame
    public async loadAndSelectRecord(dataKey: string, _deselectCurrent?: boolean): Promise<void> {
        this._chartSettingsManager.loadSeparateProfile(dataKey);
    }
    //- ISpecificallyLoadableFrame

    /**
     * Nastaveni akci pro Toolbary
     */
    protected async _initializeFormUI(): Promise<void> {
        if (!this.menu) // Pokud už je menu nastaveno, nevytváří se nové. Tato možnost zavedena pro TreeFrame, kde se do menu GLF přiřadí menu TF již před inicializací GLF.
            this.menu = this.ownerForm.formUI.createFrameMenu(this);

        if (!this.menu)
            throw new InvalidOperationException("Unable to create frame menu");

        //změny na grafech je možné provádět jen u grafů vytvořených uživatelem. Graf z formuláře z XGenu nelze měnit
        if (this._isInModalWindow) {
            if (!this.hideButtons.save) {
                this._saveAction = this._createStandardAction(ActionType.standardSaveAll, async () => {
                    await this._chartSettingsManager.saveSeparateProfile();
                    this._saveAction.visible = false;
                }, true, true, false);
            }

            DIContainer.instance.create(ISystemOperationRights).get(SystemOperation.EDITIMPPROFILE).then(result => {
                this._canEditProfile = result;

                if (result) {
                    this._moreAction = this._createStandardAction(ActionType.standardMoreSettings, async () => {
                        this._showMoreActionDropDown();
                    });
                } else {
                    //potřebuji, aby existovala nějaká akce, aby se zobrazila skupina akcí, ve které je název grafu
                    this._createStandardAction(ActionType.standardChartName, undefined, true, false);
                }
            });
            this._settingsAction = this._createStandardAction(ActionType.standardChartSettings, async () => this.showSettingPanel());
        }

        const chartNameBuilder = new ChartNameBuilder(this);
        chartNameBuilder.changed.add((_sender, name) => {
            this._chartSettingsManager.chartSettings.chartName = name;
            this._showSaveButton();
        });
        this.menu.setProperty("ChartName", chartNameBuilder);

        // toolbar musí být vidět, pokud není nastaveno, aby byl skrytý
        this.menu.setVisibility(!this.hideToolBar);
    }

    private _initializeChartSettingsPanel(): void {
        this._chartSettingsPanel = new ChartSettingsPanel();
        this.ownerForm.addSidePanel(this._chartSettingsPanel);
    }

    protected _initializeListeners(): void {
        this._chartSettingsPanel.closed.add(() => {
            this._settingsAction.toolbarButton.element.classList.remove("isActive");
        });

        this.frameProfileChanged.add(() => {
            this._showSaveButton();
        });

        if (this.masterFrame)
            this.masterFrame.actualObjectChanged.add(() => this.refreshData());
    }

    private async _initializeChartSetting(): Promise<void> {
        const attributes = Array.from(this.element.querySelectorAll("[data-tscitem='ChartFrameAttribute']"));

        //pokud není uveden typ grafu přímo na chartFrame, beru jako základní typ (udává základní vzhled grafu) z prvního atributu
        let chartType: ChartType;
        if (this.element.getAttribute("data-chartType"))
            chartType = this._converChartType(this.element.getAttribute("data-chartType"));
        else if (attributes && attributes.length > 0) {
            //pokud se nejedná o typ grafu koláč nebo kobliha, tak jako výchozí použiju bar, ten umožňuje kombinovat sloupcový a čárový graf
            const chType = this._converChartType(attributes[0].getAttribute("data-charttype"));
            if (chType == ChartType.pie || chType == ChartType.doughnut)
                chartType = chType;
            else
                chartType = ChartType.bar;
        } else
            chartType = ChartType.bar;

        this._chartSettingsManager.chartSettings = {
            frameName: this.frameName,
            masterFrameName: this.masterFrameName,
            objectName: this.element.getAttribute("data-objectname"),
            chartType: chartType,
            xAxisAttr: this.element.getAttribute("data-xaxisattr"),
            showLegend: DataConverter.toBoolean(this.element.getAttribute("data-showlegend"), true),
            showAxisValues: DataConverter.toBoolean(this.element.getAttribute("data-showaxisvalues"), true),
        } as IChartProfileInfo;

        if (this.masterFrame && this.masterFrame.currentDataGrid) {
            this._chartSettingsManager.chartSettings.condition = this.masterFrame.currentDataGrid.dataSource.getFilterCondition();
            this._condition = this.masterFrame.currentDataGrid.dataSource.getOriginalCondition();
            this._conditionParams = [...this.masterFrame.currentDataGrid.dataSource.conditionParams];
        }

        this._chartSettingsManager.chartSettings.dataSets = [];

        for (const attr of attributes) {
            const type: ChartType = this._converChartType(attr.getAttribute("data-charttype")); //EnumUtils.getEnumValueFromName(ChartType, attr.getAttribute("data-charttype").toLowerCase());
            if (type && !this._chartSettingsManager.chartSettings.chartType)
                this._chartSettingsManager.chartSettings.chartType = type;

            let aggrFunc = attr.getAttribute("data-aggregatefunction");
            if (aggrFunc == null || aggrFunc == "")
                aggrFunc = "count";

            const ds: IChartColumnDefinition = {
                aggregateFunction: EnumUtils.getEnumValueFromName(AggregationFunction, aggrFunc.toUpperCase()),
                displayFormat: attr.getAttribute("data-displayformat"),
                condition: attr.getAttribute("data-condition"),
                chartColor: this._convertColor(attr.getAttribute("data-chartcolor")),
                chartType: type,
                attrName: attr.getAttribute("data-name"),
                dataType: "", //poviny atribut z IColumnDefinition
                caption: await Localization.getTextPromise(attr.getAttribute("data-captionlockey")),
                fill: false,
            };

            this._chartSettingsManager.chartSettings.dataSets.push(ds);
        }

        if (!this._chartSettingsManager.chartSettings.chartType)
            this._chartSettingsManager.chartSettings.chartType = ChartType.bar;

        if (this._chartSettingsManager.chartSettings.dataSets.length > 0)
            this.refreshData();
    }

    private _initializeChartManager(): void {
        //pokud je chartFrame součástí XGen formuláře bere se formName z tohoto formuláře
        //pokud je otevřen jako samostatný formulář z listFramu, bere se jako nazev formuláře listFramu
        let formName: string;
        let masterFrame;
        if (this.ownerForm.formName && !this._isInModalWindow) { //Pokud je chartFrame zobrazen v samostatném modálním okně (_IsInFrame==true), pak se musí použít formName z this.ownerForm.ownerForm
            formName = this.ownerForm.formName;
            masterFrame = this.ownerForm.masterableFrames.find(f => f.frameName == this.masterFrameName);
        } else if (this.ownerForm.ownerForm) {
            formName = this.ownerForm.ownerForm.formName;
            masterFrame = this.ownerForm.ownerForm.masterableFrames.find(f => f.frameName == this.masterFrameName);
        }

        if (masterFrame instanceof GenericListFrame)
            this.masterFrame = masterFrame;

        this._chartSettingsManager = new ChartSettingsManager(formName, this.masterFrame as GenericListFrame, AppContext.instance.currentUser?.id);

        this._chartSettingsManager.settingsChanged.add(() => {
            this.refreshData();
            this._showSaveButton();
        });
        this._chartSettingsManager.profileLoaded.add(() => {
            this.chartNameChanged.trigger(this, this._chartSettingsManager.chartSettings);
            this._chartSettingsPanel.chartSettingsManager = this._chartSettingsManager;
            this.refreshData();
        });
    }

    private _initializeMainDiv(): void {
        this._mainDiv = document.createElement("div");
        this._mainDiv.classList.add("main-chart-div");
        this.element.appendChild(this._mainDiv);

        this._emptyMessage.visible = true;
        this._emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
        this._mainDiv.append(this._emptyMessage);

        this._chartElement = createElement({ type: "tsc-ChartElement" }) as ChartElement;
        this._mainDiv.append(this._chartElement);
    }

    private async _showMoreActionDropDown(): Promise<void> {
        if (this._dropDownMenu && this._dropDownMenu.isOpened) {
            this._dropDownMenu.close();
            return;
        } else {
            this._dropDownMenu = new DropDownMenu();

            const checkItem = new DropDownMenuCheckboxItem();
            checkItem.setCaption(await Localization.getTextPromise("GUI-675712"), "GUI-675712"); // Implementační
            checkItem.setLocalizedCaption("GUI-675712");
            checkItem.isChecked = this._chartSettingsManager.isImplementationChart;
            checkItem.value = this._chartSettingsManager.isImplementationChart;
            checkItem.addEventListener("click", () => {
                this._chartSettingsManager.isImplementationChart = !checkItem.isChecked;
                this._showSaveButton();
            });
            this._dropDownMenu.addItem(checkItem);
            //click přímo na checkbox nevyvolá událost, tak pro jistotu nastavuji hodnoty při zavření dropdownmenu
            this._dropDownMenu.addEventListener("visibility", () => {
                if (this._dropDownMenu.visible)
                    return;

                if (this._chartSettingsManager.isImplementationChart != checkItem.isChecked)
                    this._showSaveButton();

                this._chartSettingsManager.isImplementationChart = checkItem.isChecked;
            });

            this._dropDownMenu.show(this._moreAction.toolbarButton.element, null);
        }
    }

    /**
     * Zobrazí panel s nastavenim grafu
     * */
    public showSettingPanel(): void {
        if (this._chartSettingsPanel.visible) {
            this._chartSettingsPanel.hide();
            this._settingsAction.toolbarButton.element.classList.remove("isActive");
        } else {
            this._chartSettingsPanel.chartSettingsManager = this._chartSettingsManager;
            this._chartSettingsPanel.show();

            this._settingsAction.toolbarButton.element.classList.add("isActive"); //po otevreni panelu musi ikona zustat aktivni
        }
    }

    private _showSaveButton(): void {
        if (this._saveAction) {
            if (!this._chartSettingsManager.isImplementationChart || this._canEditProfile)
                this._saveAction.visible = true;
            //pokud je název grafu prázdný, nesmí být uložen
            if (!this._chartSettingsManager.chartSettings.chartName)
                this._saveAction.visible = false;
        }
    }

    /**
     * Snaží se řešit zpětnou kompatibilitu.
     * Dříve bylo obsahem výčtový typ Color (clBlack, clMaroon, clGreen, clOlive, clNavy, clPurple, clTeal, clGray, clSilver, clRed, clLime, clYellow, clBlue, clFuchsia, clAqua, clLtGray, clDkGray, clWhite, clMoneyGreen, clSkyBlue, clCream, clMedGray)
     * a teď se používá typ Flag (fBlue, fRed), který má méně a některé jiné položky
     * ale chart.js nejlépe pracuje když do něj vstupuje rgb hodnota barvy.
     * @param value Hodnota výčtového typu Color, nebo selektor barvy (definovaný v CSS).
     * @returns Vrací selektor barvy nebo undefined, pokud se barva v paletě MultiWebu nevyskytuje.
     */
    private _convertColor(value: string): string | undefined {
        let processedVal: string = value;

        if (StringUtils.isNullOrEmpty(value)) {
            if (!this._chartColors?.primaryColors || this._chartColors.primaryColors.length === 0)
                return undefined;

            processedVal = this._chartColors.primaryColors[0].className;
        } else if (value.startsWith("cl")) {
            const colorName = value.substr(2).toLowerCase();
            processedVal = this._chartColors.includesColor(colorName) ? colorName : undefined;
        } else if (!value.startsWith("f")) {
            const colorName = value.substr(1).toLowerCase();
            processedVal = this._chartColors.includesColor(colorName) ? colorName : undefined;
        }

        return processedVal;
    }

    private _converChartType(value: string): ChartType {
        if (value == null || value == "")
            return ChartType.bar;

        value = value.toLowerCase();
        if (value.startsWith("cht"))
            return ChartType[value.substr(3) as ChartType];

        return ChartType[value as ChartType];
    }

    public async initialize(): Promise<void> {
        await FrameSharedMethods.runWhenFirstShown(this, () => this._initializeVisual());
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        UIManager.instance.showLoader();

        try {
            //inicializace a nastavení hlavního divu
            this._initializeMainDiv();
            //inicializace settings manageru
            this._initializeChartManager();
            //nacte nastaveni grafu
            this._initializeChartSetting();
            //vytvoreni menu
            await this._initializeFormUI();
            //inicializese settingsPanelu
            this._initializeChartSettingsPanel();
            //navazani listeneru
            this._initializeListeners();

            this._isInitialized = true;
            //this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _createStandardAction(type: ActionType, prms: () => Promise<void>, createCondition: boolean = true, defaultEnabled: boolean = true, defaultVisible: boolean = true): StandardAction {
        if (createCondition) {
            const action = FrameSharedMethods.createStandardAction(type, prms, this);
            action.enabled = defaultEnabled;
            action.enabledVisual = defaultEnabled;
            action.visible = defaultVisible;
            this.menu.addAction(action);
            return action;
        } else
            return undefined;
    }

    public override dispose(): void {
        this._classAttributeObserver.disconnect(); // ruší MutationObserver na sledování změn atributu "class" elementu "html"
        super.dispose();
    }
}

/** Enum s přepsanými selectory barev, které se nacházejí v CSS stylech, slouží pro lepší srozumitelnost */
const enum LessThemeSelector {
    /** nahrazuje selector pro barvu "White & Black", slouží tam, kde chceme vracet pro světlé téma bílou barvu a pro tmavé téma černou barvu */
    whiteThenBlack = "wb",
    /** nahrazuje selector pro barvu "Black & White", slouží tam, kde chceme vracet pro světlé téma černou barvu a pro tmavé téma bílou barvu */
    blackThenWhite = "bw",
}

/**
* Slouží ke zobrazení vlastního textu pro prázdný pravý seznam
*/
class ChartEmptyDatagridMessage extends EmptyDataGridMessage {
    protected override _constructor(): void {
        super._constructor();
    }

    protected override async _setMessageType(messageType: EmptyDatagridMessageType): Promise<void> {
        this._iconElement.classList.remove("InitialEmpty", "FilterEmpty", "FulltextEmpty", "data-loading", "chartEmpty");

        if (messageType === EmptyDatagridMessageType.initialEmpty) {
            this._iconElement.classList.add("chartEmpty");
            Localization.addLocData(this._descElement, "GUI-672823"); //Zvolte data grafu
        } else {
            super._setMessageType(messageType);
        }
    }
}

export interface IGraphDataSourceParams extends IDBDataSourceParams {
    dataSet: IChartColumnDefinition;
}

export class GraphDBDataSource extends DBDataSource {
    /** Dataset, pro který jsou data načítána */
    private _dataSet: IChartColumnDefinition;
    public get dataSet(): IChartColumnDefinition {
        return this._dataSet;
    }

    public override set orderBy(values: IOrderBy[]) {
        this._orderBy = values;
        if (values && values.length > 0) {
            const resultOrderBy: string[] = [];

            for (const orderByItem of values) {
                resultOrderBy.push(orderByItem.order == OrderType.descending ? `DESC ${orderByItem.attributeName}` : orderByItem.attributeName);
            }

            this._internalOrderBy = resultOrderBy;
        } else {
            this._internalOrderBy = [this.dataKey]; // při nezadaném sloupci pro třídění se defaultně použije sloupec ID
        }

        this.waitForReload().then(() => {
            if (this._internalRows && this._internalRows.length > 0)
                this.loadNewData(false);
        });
    }

    constructor(params: IGraphDataSourceParams) {
        super(params);

        this._dataSet = params.dataSet;
    }
}

registerCustomElement("tsc-ChartEmptyDatagridMessage", ChartEmptyDatagridMessage);
